import AutomationCard from '@/components/views/AutomationsView/AutomationCard';
import AutomationsTable from '@/components/views/AutomationsView/AutomationsTable';
import { hasAutomationConfigDrawer } from '@/components/Drawers/AutomationConfigDrawer';
import { useSetAutomationEnabledMutation } from '@/features/automations/automationsApi';
import { openDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { setAutomationsView } from '@/features/userPrefs/userPrefsSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useAutomationContext } from '@/context/automationContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import {
	Box,
	CircularProgress,
	Grid,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const VIEW_CARDS = 'cards';
const VIEW_TABLE = 'table';
const DEBTOR_REMINDERS_AUTOMATION = 'debtorReminders';

const AutomationsGallery = () => {
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const viewMode = useSelector((state) =>
		state.userPrefs.automationsView === VIEW_TABLE ? VIEW_TABLE : VIEW_CARDS,
	);
	const { getFeatureFlags } = useFeatureFlagContext();
	const { getAutomations, isLoading: isAutomationsLoading, isError } = useAutomationContext();
	const isAutomatedDebtorsEnabled = Boolean(getFeatureFlags('automatedDebtors')?.active);
	const canEditAutomations = Boolean(getFeatureFlags('editAutomations')?.active);

	const { data: userDetails, isLoading: isUserLoading } = useGetUserDetailsQuery(
		{ email },
		{ skip: !email },
	);

	const brokerGroupIds = userDetails?.brokerGroupArray;

	const [updatingIds, setUpdatingIds] = useState([]);
	const automations = getAutomations();

	const isLoading = isUserLoading || isAutomationsLoading;

	const visibleAutomations = useMemo(
		() =>
			automations.filter(
				(automation) =>
					automation.name !== DEBTOR_REMINDERS_AUTOMATION || isAutomatedDebtorsEnabled,
			),
		[automations, isAutomatedDebtorsEnabled],
	);

	const [setAutomationEnabled] = useSetAutomationEnabledMutation();

	const handleViewModeChange = (_event, nextView) => {
		if (!nextView) return;
		dispatch(setAutomationsView(nextView));
	};

	const handleEditAutomation = (automation) => {
		if (!automation?.editable || !hasAutomationConfigDrawer(automation.name)) return;
		dispatch(
			openDialog({
				dialogName: 'automation',
				data: { name: automation.name },
			}),
		);
	};

	// Switch shows primary brokerGroupId (getAutomations). Write updates every group in brokerGroupArray.
	const handleBrokerageToggle = useCallback(
		async (automationId, enabled) => {
			if (!canEditAutomations || brokerGroupIds.length === 0 || updatingIds.includes(automationId)) {
				return;
			}

			setUpdatingIds((prev) => [...prev, automationId]);
			try {
				await setAutomationEnabled({
					automationId,
					brokerGroupIds,
					enabled,
				}).unwrap();
			} catch (error) {
				dispatch(
					showToast({
						message: error?.error ?? error?.message ?? 'Failed to update automation',
						error: true,
					}),
				);
			} finally {
				setUpdatingIds((prev) => prev.filter((id) => id !== automationId));
			}
		},
		[brokerGroupIds, canEditAutomations, dispatch, setAutomationEnabled, updatingIds],
	);

	const cardColumns = Math.min(5, Math.max(1, visibleAutomations.length));

	return (
		<Box
			sx={{
				width: '100%',
				...(viewMode === VIEW_CARDS && {
					maxWidth: 1400,
					mx: 'auto',
				}),
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					width: '100%',
					mb: 2,
				}}
			>
				<ToggleButtonGroup
					exclusive
					size='small'
					value={viewMode}
					onChange={handleViewModeChange}
					aria-label='Automations view mode'
					sx={(theme) => ({
						'& .MuiToggleButton-root.Mui-selected': {
							color: theme.palette.primary.main,
							backgroundColor: 'transparent',
							'&:hover': {
								backgroundColor: 'action.hover',
								color: theme.palette.primary.main,
							},
						},
						'& .MuiToggleButton-root.Mui-selected .MuiSvgIcon-root': {
							color: theme.palette.primary.main,
						},
					})}
				>
					<ToggleButton value={VIEW_CARDS} aria-label='Card view'>
						<GridViewIcon fontSize='small' />
					</ToggleButton>
					<ToggleButton value={VIEW_TABLE} aria-label='Table view'>
						<TableRowsIcon fontSize='small' />
					</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{isLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
					<CircularProgress size={28} />
				</Box>
			) : isError ? (
				<Typography color='error'>Failed to load automations.</Typography>
			) : visibleAutomations.length === 0 ? (
				<Typography color='text.secondary'>No automations available.</Typography>
			) : viewMode === VIEW_TABLE ? (
				<AutomationsTable
					automations={visibleAutomations}
					updatingIds={updatingIds}
					onBrokerageToggle={handleBrokerageToggle}
					onEditAutomation={handleEditAutomation}
				/>
			) : (
				<Grid container spacing={2}>
					{visibleAutomations.map((automation) => (
						<Grid
							key={automation.id}
							size={{
								xs: 12,
								sm: 6,
								md: 4,
								lg: 12 / cardColumns,
							}}
						>
							<AutomationCard
								automation={automation}
								enabled={Boolean(automation.enabled)}
								disabled={!canEditAutomations || updatingIds.includes(automation.id)}
								canEdit={
									canEditAutomations &&
									automation?.editable &&
									hasAutomationConfigDrawer(automation.name)
								}
								onToggle={handleBrokerageToggle}
								onEdit={handleEditAutomation}
							/>
						</Grid>
					))}
				</Grid>
			)}

		</Box>
	);
};

export default AutomationsGallery;
